import { useCallback, useMemo, useRef, useState } from 'react';
import { BarChart3, DollarSign, FileText, FileUp } from 'lucide-react';
import type { ExpenseItemWithRelations, User } from '@peles/shared';
import { EntityModal } from '../common/EntityModal';
import { useDialog } from '../../context/DialogContext';
import { useDeleteExpenseItem, useExpenseConfig, useExpenseItems } from '../../hooks/useExpenses';
import { useVendorCategories } from '../../hooks/useVendors';
import { useEvents } from '../../hooks/useEvents';
import { ExpenseFilters, type ExpenseFiltersState } from './ExpenseFilters';
import { ExpenseTable } from './ExpenseTable';
import { ExpenseDetailPanel } from './ExpenseDetailPanel';
import { CreateExpensePanel } from './CreateExpensePanel';
import { QuoteImportModal } from './QuoteImportModal';
import { InvoiceImportModal } from './InvoiceImportModal';
import { CompareQuotesModal } from './CompareQuotesModal';
import { EventSelectorModal } from './EventSelectorModal';
import { ItemizedListModal } from '../planning/ItemizedListModal';

type EntityFilter = 'EVENT' | 'VENDOR';

interface ExpenseListViewProps {
  /** Pre-scope filter — locks scope and hides the all-events selector. */
  entityType?: EntityFilter;
  /** Pre-scope id (eventId when entityType='EVENT', vendorId when 'VENDOR'). */
  entityId?: string;
  /** Show the event-name column. Defaults to: hidden when entityType='EVENT'. */
  showEventColumn?: boolean;
  /** Current user — needed for admin-only actions like delete. */
  currentUser?: User;
}

type PanelState =
  | { mode: 'closed' }
  | { mode: 'edit'; expenseItemId: string }
  | { mode: 'create'; selectedEventId: string };

function formatCurrency(amount: number): string {
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Unified expense list view — same component on the standalone /expenses page,
 * the event detail page (pre-scoped by event) and the vendor detail page
 * (pre-scoped by vendor). Owns its own filters, summary bar, header buttons,
 * and modals (edit / create / import / compare).
 */
export function ExpenseListView({
  entityType,
  entityId,
  showEventColumn,
  currentUser,
}: ExpenseListViewProps) {
  const isEventScoped = entityType === 'EVENT' && !!entityId;
  const isVendorScoped = entityType === 'VENDOR' && !!entityId;
  const showEventCol = showEventColumn ?? !isEventScoped;

  const [panel, setPanel] = useState<PanelState>({ mode: 'closed' });
  const [quoteImportOpen, setQuoteImportOpen] = useState(false);
  const [invoiceImportOpen, setInvoiceImportOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [eventSelectorFor, setEventSelectorFor] = useState<'create' | 'import' | 'invoice' | null>(
    null,
  );
  const [selectedEventFilter, setSelectedEventFilter] = useState('');
  // Zoom into the itemized list, pre-filtered to a single expense line.
  const [itemizedForExpense, setItemizedForExpense] = useState<ExpenseItemWithRelations | null>(
    null,
  );
  const [filters, setFilters] = useState<ExpenseFiltersState>({
    search: '',
    vendorCategoryIds: [],
    statusIds: [],
  });
  const hasUnsavedRef = useRef(false);
  const { confirm } = useDialog();

  // ─── Effective scope ────────────
  // Route entity wins, then event-filter dropdown when not pre-scoped.
  const effectiveEventId = isEventScoped ? entityId : selectedEventFilter || undefined;
  const apiFilters = useMemo(() => {
    if (isEventScoped) return { eventId: entityId };
    if (isVendorScoped) return { vendorId: entityId };
    if (selectedEventFilter) return { eventId: selectedEventFilter };
    return undefined;
  }, [isEventScoped, isVendorScoped, entityId, selectedEventFilter]);

  // ─── Data ────────────────────────
  const { data: items = [], isLoading } = useExpenseItems(apiFilters);
  const { data: config } = useExpenseConfig();
  const { data: categories = [] } = useVendorCategories();
  const { data: allEvents = [] } = useEvents();
  const deleteExpenseItem = useDeleteExpenseItem();

  // ─── Client-side filter ──────────
  const filteredItems = useMemo(() => {
    let result = items;
    if (filters.vendorCategoryIds.length > 0) {
      result = result.filter((item) => filters.vendorCategoryIds.includes(item.vendorCategoryId));
    }
    if (filters.statusIds.length > 0) {
      result = result.filter((item) => filters.statusIds.includes(item.statusId));
    }
    if (filters.search) {
      const term = filters.search.toLowerCase();
      result = result.filter(
        (item) =>
          (item.vendorName && item.vendorName.toLowerCase().includes(term)) ||
          (item.itemName && item.itemName.toLowerCase().includes(term)) ||
          (item.eventName && item.eventName.toLowerCase().includes(term)),
      );
    }
    return result;
  }, [items, filters]);

  // ─── Summary ─────────────────────
  // `openBalance` is the total of expenses not yet paid (sum of per-item
  // balance, where balance = price - totalPaid). Using the item-level field
  // matches whatever the API has computed for client-paid / reimbursement
  // logic, rather than re-deriving it here.
  const summary = useMemo(() => {
    const totalExpenses = items.reduce((sum, item) => sum + item.price, 0);
    const totalPaid = items.reduce((sum, item) => sum + item.totalPaid, 0);
    const openBalance = items.reduce((sum, item) => sum + item.balance, 0);
    return { totalExpenses, totalPaid, openBalance };
  }, [items]);

  // ─── Compare-quotes ──────────────
  // Per-category, so only meaningful when exactly one category is selected.
  const compareCategoryId =
    filters.vendorCategoryIds.length === 1 ? filters.vendorCategoryIds[0] : null;
  const selectedCategoryName = useMemo(() => {
    if (!compareCategoryId) return '';
    return categories.find((c) => c.id === compareCategoryId)?.name ?? '';
  }, [compareCategoryId, categories]);

  // ─── Unsaved-edit guard ──────────
  const confirmUnsaved = useCallback(async (): Promise<boolean> => {
    if (hasUnsavedRef.current) {
      return confirm({
        title: 'Unsaved Changes',
        message: 'You have unsaved changes. Discard them?',
        confirmLabel: 'Discard',
        variant: 'warning',
      });
    }
    return true;
  }, [confirm]);

  const handleSelectExpense = useCallback(
    async (id: string) => {
      if (!(await confirmUnsaved())) return;
      hasUnsavedRef.current = false;
      setPanel({ mode: 'edit', expenseItemId: id });
    },
    [confirmUnsaved],
  );

  const handleClosePanel = useCallback(async () => {
    if (!(await confirmUnsaved())) return;
    hasUnsavedRef.current = false;
    setPanel({ mode: 'closed' });
  }, [confirmUnsaved]);

  const handleUnsavedChange = useCallback((dirty: boolean) => {
    hasUnsavedRef.current = dirty;
  }, []);

  const handleDelete = useCallback(
    (id: string) => {
      deleteExpenseItem.mutate(id);
    },
    [deleteExpenseItem],
  );

  // ─── Header actions ──────────────
  const handleCreateClick = useCallback(async () => {
    if (!(await confirmUnsaved())) return;
    hasUnsavedRef.current = false;
    if (isEventScoped && entityId) {
      setPanel({ mode: 'create', selectedEventId: entityId });
    } else {
      setEventSelectorFor('create');
    }
  }, [confirmUnsaved, isEventScoped, entityId]);

  const handleImportQuoteClick = useCallback(() => {
    if (isEventScoped && entityId) {
      setQuoteImportOpen(true);
    } else {
      setEventSelectorFor('import');
    }
  }, [isEventScoped, entityId]);

  const handleImportInvoiceClick = useCallback(() => {
    if (isEventScoped && entityId) {
      setInvoiceImportOpen(true);
    } else {
      setEventSelectorFor('invoice');
    }
  }, [isEventScoped, entityId]);

  const handleEventSelected = useCallback(
    (selectedId: string) => {
      const which = eventSelectorFor;
      setEventSelectorFor(null);
      if (which === 'create') {
        setPanel({ mode: 'create', selectedEventId: selectedId });
      } else if (which === 'import') {
        setSelectedEventFilter(selectedId);
        setQuoteImportOpen(true);
      } else if (which === 'invoice') {
        setSelectedEventFilter(selectedId);
        setInvoiceImportOpen(true);
      }
    },
    [eventSelectorFor],
  );

  const handleCreated = useCallback(() => {
    setPanel({ mode: 'closed' });
  }, []);

  const importEventId = isEventScoped ? entityId : selectedEventFilter || undefined;
  const editingId = panel.mode === 'edit' ? panel.expenseItemId : null;
  const creatingForEventId = panel.mode === 'create' ? panel.selectedEventId : null;

  return (
    <>
      {/* Header — title-less in embedded mode (parent shows the section header) */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-base font-semibold text-gray-900">Expenses</h3>
        <div className="flex items-center gap-2">
          {effectiveEventId && compareCategoryId && (
            <button
              onClick={() => setCompareOpen(true)}
              className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
              title="Compare quotes for this category"
            >
              <BarChart3 size={16} />
              <span className="hidden sm:inline">Compare</span>
            </button>
          )}
          <button
            onClick={handleImportInvoiceClick}
            className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
          >
            <FileText size={16} />
            <span className="hidden sm:inline">Import Invoice</span>
          </button>
          <button
            onClick={handleImportQuoteClick}
            className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
          >
            <FileUp size={16} />
            <span className="hidden sm:inline">Import Quote</span>
          </button>
          <button
            onClick={handleCreateClick}
            className="flex items-center gap-2 px-3 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700"
          >
            <DollarSign size={16} />
            <span className="hidden sm:inline">Add Expense</span>
          </button>
        </div>
      </div>

      {/* Event filter dropdown — only when not pre-scoped */}
      {!isEventScoped && !isVendorScoped && (
        <div className="mb-4">
          <select
            value={selectedEventFilter}
            onChange={(e) => setSelectedEventFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label="Filter by event"
          >
            <option value="">All Events</option>
            {allEvents.map((event) => (
              <option key={event.id} value={event.id}>
                {event.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Summary Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
        <div className="bg-white rounded-lg shadow px-4 py-3">
          <div className="text-xs text-gray-500">Total Expenses</div>
          <div className="text-lg font-semibold text-gray-900">
            {formatCurrency(summary.totalExpenses)}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow px-4 py-3">
          <div className="text-xs text-gray-500">Total Paid</div>
          <div className="text-lg font-semibold text-gray-900">
            {formatCurrency(summary.totalPaid)}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow px-4 py-3 col-span-2 sm:col-span-1">
          <div className="text-xs text-gray-500">Open Balance</div>
          <div
            className={`text-lg font-semibold ${summary.openBalance > 0 ? 'text-red-600' : 'text-green-600'}`}
          >
            {formatCurrency(summary.openBalance)}
          </div>
        </div>
      </div>

      {/* Filters */}
      <ExpenseFilters
        filters={filters}
        onFiltersChange={setFilters}
        categories={categories}
        statuses={config?.statuses ?? []}
      />

      {/* Table */}
      <ExpenseTable
        items={filteredItems}
        selectedId={editingId}
        onSelect={handleSelectExpense}
        onDelete={handleDelete}
        onZoomItems={setItemizedForExpense}
        isLoading={isLoading}
        currentUser={currentUser}
        showEventName={showEventCol}
      />

      {/* Edit modal */}
      <EntityModal
        open={editingId !== null}
        onClose={handleClosePanel}
        title="Expense Details"
        size="xl"
      >
        {editingId && currentUser && (
          <ExpenseDetailPanel
            key={editingId}
            expenseItemId={editingId}
            onClose={handleClosePanel}
            onUnsavedChange={handleUnsavedChange}
            currentUser={currentUser}
          />
        )}
      </EntityModal>

      {/* Create modal */}
      <EntityModal
        open={creatingForEventId !== null}
        onClose={handleClosePanel}
        title="Add Expense"
        size="lg"
      >
        {creatingForEventId && (
          <CreateExpensePanel
            eventId={creatingForEventId}
            onClose={handleClosePanel}
            onCreated={handleCreated}
          />
        )}
      </EntityModal>

      {/* Import modals */}
      {importEventId && (
        <QuoteImportModal
          eventId={importEventId}
          isOpen={quoteImportOpen}
          onClose={() => setQuoteImportOpen(false)}
          onImported={() => setQuoteImportOpen(false)}
        />
      )}
      {importEventId && (
        <InvoiceImportModal
          eventId={importEventId}
          isOpen={invoiceImportOpen}
          onClose={() => setInvoiceImportOpen(false)}
          onImported={() => setInvoiceImportOpen(false)}
        />
      )}

      {/* Compare quotes — only when an event is in scope and exactly one category is selected */}
      {effectiveEventId && compareCategoryId && (
        <CompareQuotesModal
          eventId={effectiveEventId}
          categoryId={compareCategoryId}
          categoryName={selectedCategoryName}
          isOpen={compareOpen}
          onClose={() => setCompareOpen(false)}
        />
      )}

      {/* Itemized list zoom — pre-filtered to the clicked expense line */}
      {itemizedForExpense && (
        <ItemizedListModal
          open
          onClose={() => setItemizedForExpense(null)}
          eventId={itemizedForExpense.eventId}
          initialFilters={{ expenseItemIds: [itemizedForExpense.id] }}
          title={`Itemized List — ${itemizedForExpense.itemName ?? itemizedForExpense.vendorCategoryName}`}
        />
      )}

      <EventSelectorModal
        isOpen={eventSelectorFor !== null}
        onClose={() => setEventSelectorFor(null)}
        onSelect={handleEventSelected}
        title={
          eventSelectorFor === 'create'
            ? 'Select Event for New Expense'
            : eventSelectorFor === 'invoice'
              ? 'Select Event for Invoice Import'
              : 'Select Event for Import'
        }
      />
    </>
  );
}
